// SSE / timeline 事件 → TerminalEntry 的映射 / Event → TerminalEntry mapping.
// 所有函数都是 pure，便于单测。阶段名在 UI 中统一展示英文。
// Pure functions; phase labels are displayed in English across the UI.

import type {
  SseEvent,
  TerminalEntry,
  TerminalSeverity,
  TerminalTab,
  TimelineItem,
} from "@/features/log-terminal/terminal-types";

// 阶段英文名 / English phase labels.
const PHASE_LABELS: Record<string, string> = {
  INIT: "Initialization",
  SEED: "Seed Injection",
  RIPPLE: "Ripple Propagation",
  OBSERVE: "Global Observation",
  DELIBERATE: "Jury Deliberation",
  SYNTHESIZE: "Synthesis",
};

/** 把 arm 字段归一化到 Tab / Normalize any arm variant to a TerminalTab. */
export function armToTab(arm: string | null | undefined): TerminalTab {
  if (!arm) return "system";
  const last = arm.slice(-1).toUpperCase();
  if (last === "A") return "planA";
  if (last === "B") return "planB";
  return "system";
}

/** 获取阶段展示标签，未知 → 原值 / null → "" */
export function phaseChineseLabel(phase: string | null | undefined): string {
  if (!phase) return "";
  return PHASE_LABELS[phase] ?? phase;
}

interface MapContext {
  /** epoch ms；通常调用方传 Date.now()，测试注入固定值 */
  now: number;
}

/** SSE envelope → TerminalEntry；未识别返回 null */
export function sseEventToEntry(event: SseEvent, ctx: MapContext): TerminalEntry | null {
  const tab = armToTab(event.arm);
  const payload = event.payload ?? {};
  const timestamp = sseTimestamp(event, ctx.now);

  // 1) progress.* 系列
  if (event.type.startsWith("progress.")) {
    return mapProgress(event.type.slice("progress.".length), tab, payload, event, timestamp);
  }

  // 2) run.*
  if (event.type.startsWith("run.")) {
    return mapRun(event.type, tab, event, timestamp);
  }

  // 3) report.*
  if (event.type.startsWith("report.")) {
    return mapReport(event.type, event, timestamp);
  }

  // 4) lifecycle（后端 lifecycle 事件通过 simulation_orchestrator 已转成 run.* 或原样透传）
  // 对未知类型放行 null，由 buffer 层决定是否保留。
  return null;
}

/** Timeline JSON item → TerminalEntry；timestamp 走 ISO → epoch */
export function timelineItemToEntry(item: TimelineItem): TerminalEntry | null {
  const tab = armToTab(item.arm);
  const timestamp = Date.parse(item.timestamp);

  // timeline 里 type 已去掉 "progress." 前缀，先尝试 progress 映射
  const progressKinds = new Set([
    "phase_start", "phase_end",
    "wave_start", "wave_end",
    "round_start", "round_end",
    "agent_activated", "agent_responded",
  ]);
  if (progressKinds.has(item.type)) {
    // exactOptionalPropertyTypes：只把非 undefined 值塞进 payload / synthetic
    const payload: NonNullable<SseEvent["payload"]> = { detail: item.detail };
    if (item.phase !== undefined) payload.phase = item.phase;
    if (item.progress !== undefined) payload.progress = item.progress;
    if (item.wave !== undefined) payload.wave = item.wave;
    if (item.total_waves !== undefined) payload.total_waves = item.total_waves;
    const synthetic: SseEvent = {
      type: `progress.${item.type}`,
      payload,
    };
    if (item.arm) synthetic.arm = item.arm;
    return sseEventToEntry(synthetic, { now: timestamp });
  }

  // lifecycle 类型：queued / running / job_created / job_completed / job_failed /
  // report_started / report_ready / report_failed …
  const key = rawKeyFor(item.arm ?? "system", timestamp, item.type, undefined, undefined);
  return {
    tab,
    timestamp,
    severity: "system",
    density: "compact",
    text: `${item.message || item.type}`.trim(),
    rawKey: key,
  };
}

// ────────────────────────── internal helpers ──────────────────────────

function sseTimestamp(event: SseEvent, fallback: number): number {
  if (!event.timestamp) return fallback;
  const parsed = Date.parse(event.timestamp);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mapProgress(
  kind: string,
  tab: TerminalTab,
  payload: NonNullable<SseEvent["payload"]>,
  evt: SseEvent,
  timestamp: number,
): TerminalEntry | null {
  const detail = (payload.detail ?? {}) as Record<string, unknown>;
  const phase = payload.phase;
  const wave = payload.wave;
  const totalWaves = payload.total_waves;
  const armKey = evt.arm ?? "system";
  const phaseKey = typeof phase === "string" && phase ? `phase=${phase}` : undefined;

  const baseKey = (extra?: string) =>
    rawKeyFor(armKey, timestamp, `progress.${kind}`, wave, extra);

  switch (kind) {
    case "phase_start": {
      const entry: TerminalEntry = {
        tab,
        timestamp,
        severity: "phase",
        density: "compact",
        text: `▶ ${phaseChineseLabel(phase)} started`,
        rawKey: baseKey(phaseKey),
      };
      if (phase) entry.phase = phase;
      return entry;
    }
    case "phase_end": {
      let suffix = "";
      if (phase === "INIT") {
        suffix = ` — Star×${detail["star_count"] ?? "?"} Sea×${detail["sea_count"] ?? "?"} estimated ${detail["estimated_waves"] ?? "?"} waves`;
      } else if (phase === "SEED") {
        suffix = ` — energy=${detail["seed_energy"] ?? "?"}`;
      } else if (phase === "RIPPLE") {
        suffix = ` — ${detail["effective_waves"] ?? "?"} waves executed`;
      } else if (phase === "DELIBERATE") {
        // DELIBERATE phase_end detail (runtime.py: build from deliberation_summary)：
        // { rounds, converged, consensus_points, dissent_points, final_positions }
        // rounds 即 rounds_executed，可能为 undefined；converged 是布尔。
        // DELIBERATE phase_end detail keys: rounds / converged / consensus_points / dissent_points / final_positions.
        const rounds = detail["rounds"];
        const converged = detail["converged"];
        const roundsPart = typeof rounds === "number" ? `${rounds} deliberation rounds` : "done";
        const convPart =
          converged === true ? ", converged"
            : converged === false ? ", not converged"
            : "";
        suffix = ` — ${roundsPart}${convPart}`;
      }
      const entry: TerminalEntry = {
        tab,
        timestamp,
        severity: "phase",
        density: "compact",
        text: `✓ ${phaseChineseLabel(phase)} completed${suffix}`,
        rawKey: baseKey(phaseKey),
      };
      if (phase) entry.phase = phase;
      return entry;
    }
    case "wave_start": {
      const shown = typeof wave === "number" ? wave + 1 : "?";
      const total = typeof totalWaves === "number" ? totalWaves : "?";
      return {
        tab,
        timestamp,
        severity: "wave",
        density: "compact",
        text: `━ Wave ${shown}/${total}`,
        rawKey: baseKey(),
      };
    }
    case "wave_end": {
      const terminated = detail["terminated"];
      const text = terminated
        ? `╰ propagation stopped: ${detail["reason"] ?? ""}`.trim()
        : `╰ ${detail["agent_count"] ?? 0} Agent responses`;
      return {
        tab,
        timestamp,
        severity: "wave",
        density: "compact",
        text,
        rawKey: baseKey(),
      };
    }
    case "round_start": {
      const r = detail["round_number"] ?? "?";
      const R = detail["total_rounds"] ?? "?";
      return {
        tab,
        timestamp,
        severity: "round",
        density: "compact",
        text: `━ Deliberation Round ${r}/${R} started`,
        rawKey: baseKey(`round=${r}`),
      };
    }
    case "round_end": {
      const r = detail["round_number"] ?? "?";
      const R = detail["total_rounds"] ?? "?";
      const conv = detail["converged"] ? " (converged)" : "";
      return {
        tab,
        timestamp,
        severity: "round",
        density: "compact",
        text: `╰ Deliberation Round ${r}/${R} completed${conv}`,
        rawKey: baseKey(`round=${r}`),
      };
    }
    case "agent_activated": {
      // 后端 job_manager 把 agent_id / agent_type 放 payload 顶层；
      // timeline 持久化时这两个字段不会被复制到 item 顶层也不进 detail（见 simulation_timeline._append），
      // 但保留 detail 回退以兼容任何把它们嵌入 detail 的上游。
      // agent_id/agent_type live at SSE payload top-level; fall back to detail for compat.
      const aid = payload.agent_id ?? detail["agent_id"] ?? "?";
      const atype = payload.agent_type ?? detail["agent_type"] ?? "?";
      const energy = detail["energy"] ?? "?";
      return {
        tab,
        timestamp,
        severity: "agent",
        density: "verbose",
        text: `→ activate ${atype}:${aid} (energy=${energy})`,
        rawKey: baseKey(`agent=${aid}`),
      };
    }
    case "agent_responded": {
      const aid = payload.agent_id ?? detail["agent_id"] ?? "?";
      const rtype = detail["response_type"] ?? "?";
      return {
        tab,
        timestamp,
        severity: "agent",
        density: "verbose",
        text: `← ${aid} response: ${rtype}`,
        rawKey: baseKey(`agent=${aid}:resp`),
      };
    }
    default:
      return null;
  }
}

function mapRun(
  type: string,
  tab: TerminalTab,
  evt: SseEvent,
  timestamp: number,
): TerminalEntry {
  const msg =
    type === "run.starting" ? "⟳ Simulation starting"
      : type === "run.created" ? `⟳ AgentCore job created`
      : type === "run.completed" ? "✓ Simulation completed"
      : type === "run.failed" ? `✗ Simulation failed: ${evt.error ?? ""}`.trim()
      : type === "run.cancelled" ? "⊘ Cancelled"
      : type === "run.timeout" ? "⊘ Timed out"
      : type;
  const severity: TerminalSeverity =
    type === "run.failed" || type === "run.timeout" ? "error"
      : type === "run.cancelled" ? "warn"
      : "system";
  return {
    tab,
    timestamp,
    severity,
    density: "compact",
    text: msg,
    rawKey: rawKeyFor(evt.arm ?? "system", timestamp, type, undefined, undefined),
  };
}

function mapReport(type: string, evt: SseEvent, timestamp: number): TerminalEntry {
  const text =
    type === "report.started" ? "⟳ Generating report"
      : type === "report.regenerated" ? "✓ Report generated"
      : type === "report.failed" ? `✗ Report generation failed: ${friendlyReportError(evt.error)}`.trim()
      : type;
  const severity: TerminalSeverity = type === "report.failed" ? "warn" : "system";
  return {
    tab: "system",
    timestamp,
    severity,
    density: "compact",
    text,
    rawKey: reportRawKey(type, evt, timestamp),
  };
}

function friendlyReportError(error: string | null | undefined): string {
  // 后端会保留原始诊断到 job event；终端只展示用户可执行提示。
  // Backend keeps raw diagnostics in job events; terminal shows actionable text.
  const message = String(error ?? "").trim();
  const lowered = message.toLowerCase();
  if (!message) return "Please try again later.";
  if (
    lowered.includes("runtimeclienterror") ||
    lowered.includes("starting the runtime") ||
    lowered.includes("cloudwatch") ||
    lowered.includes("job_start_failed")
  ) {
    return "The report generation service is temporarily unavailable. Please try again later; if deployment just finished, confirm AgentCore has been redeployed and is healthy.";
  }
  if (lowered.includes("unsupported_job_type") || lowered.includes("unsupported job type")) {
    return "The current AgentCore version does not support report generation. Redeploy AgentCore and try again.";
  }
  if (lowered.includes("markdown_na_leak") || lowered.includes("markdown_waiting_placeholder")) {
    return "The report still contains unresolved placeholders, so publishing was blocked. Please regenerate the report.";
  }
  return message;
}

function reportRawKey(type: string, evt: SseEvent, timestamp: number): string {
  // report.started 会在 SSE 重连时回放，用 job/simulation 稳定键去重；终态事件保留时间戳。
  // report.started is replayed after SSE reconnects; dedupe by job/simulation while terminal events keep timestamps.
  if (type !== "report.started") {
    return rawKeyFor("system", timestamp, type, undefined, undefined);
  }
  const stableId =
    evt.job_id ||
    evt.agentcore_job_id ||
    evt.simulation?.id ||
    evt.report_status ||
    "pending";
  return `system:report.started:${stableId}`;
}

function rawKeyFor(
  arm: string,
  timestamp: number,
  type: string,
  wave: number | undefined,
  extra: string | undefined,
): string {
  // 六段式 key：arm:timestamp:type:wave:round:extra；phase 事件会把 phase 写入 extra 防止同毫秒去重误杀。
  // Six-part key; phase events store phase in extra so same-ms phase rows do not dedupe each other.
  return `${arm}:${timestamp}:${type}:${wave ?? ""}::${extra ?? ""}`;
}
